import React, { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguages } from '@/hooks/useLanguages';

interface WelcomeMessageEditorProps {
  showWelcomeMessage: boolean;
  useCustomWelcomeMessage: boolean;
  welcomeMessageCustom: Record<string, string>;
  saving: boolean;
  onUpdateSetting: (key: string, value: boolean | string | object) => Promise<void>;
}

export function WelcomeMessageEditor({ 
  showWelcomeMessage, 
  useCustomWelcomeMessage, 
  welcomeMessageCustom,
  saving, 
  onUpdateSetting 
}: WelcomeMessageEditorProps) {
  const { languages } = useLanguages();
  const [customMessages, setCustomMessages] = useState(welcomeMessageCustom || {});
  const [hasChanges, setHasChanges] = useState(false);

  const handleCustomMessageChange = (languageCode: string, value: string) => {
    const updatedMessages = { ...customMessages, [languageCode]: value };
    setCustomMessages(updatedMessages);
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onUpdateSetting('welcomeMessageCustom', customMessages);
    setHasChanges(false);
  };

  const handleReset = () => {
    setCustomMessages(welcomeMessageCustom || {});
    setHasChanges(false);
  };

  const handleUseCustomToggle = (value: boolean) => {
    onUpdateSetting('useCustomWelcomeMessage', value);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="show-welcome">Welcome Message</Label>
          <p className="text-sm text-muted-foreground">
            Show the welcome message section with museum information
          </p>
        </div>
        <Switch
          id="show-welcome"
          checked={showWelcomeMessage}
          onCheckedChange={(value) => onUpdateSetting('showWelcomeMessage', value)}
          disabled={saving}
        />
      </div>

      {showWelcomeMessage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Welcome Message Content</CardTitle>
            <CardDescription>
              Choose between using the default welcome message or create custom content for each language
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="use-custom-welcome">Use Custom Message</Label>
                <p className="text-sm text-muted-foreground">
                  Enable to write custom welcome messages for each language
                </p>
              </div>
              <Switch
                id="use-custom-welcome"
                checked={useCustomWelcomeMessage}
                onCheckedChange={handleUseCustomToggle}
                disabled={saving}
              />
            </div>

            {useCustomWelcomeMessage && (
              <div className="space-y-4">
                <Tabs defaultValue={languages[0]?.code} className="w-full">
                  <TabsList className="grid w-full grid-cols-5">
                    {languages.map((lang) => (
                      <TabsTrigger key={lang.code} value={lang.code}>
                        {lang.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {languages.map((lang) => (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-2">
                      <Label htmlFor={`welcome-${lang.code}`}>
                        Welcome Message - {lang.name}
                      </Label>
                      <Textarea
                        id={`welcome-${lang.code}`}
                        placeholder={`Enter welcome message in ${lang.name}...`}
                        value={customMessages[lang.code] || ''}
                        onChange={(e) => handleCustomMessageChange(lang.code, e.target.value)}
                        disabled={saving}
                        rows={6}
                        className="min-h-[120px]"
                      />
                      <p className="text-xs text-muted-foreground">
                        You can use line breaks to create paragraphs
                      </p>
                    </TabsContent>
                  ))}
                 </Tabs>
                 
                 {hasChanges && (
                   <div className="flex gap-3 pt-4 border-t">
                     <Button onClick={handleSave} disabled={saving}>
                       {saving ? 'Saving...' : 'Save Changes'}
                     </Button>
                     <Button variant="outline" onClick={handleReset} disabled={saving}>
                       Reset
                     </Button>
                   </div>
                 )}
               </div>
             )}
           </CardContent>
         </Card>
       )}
     </div>
   );
 }